import {
  DestroyRef,
  inject,
  Injectable,
  signal
} from '@angular/core';
import { UploadJob, UploadJobStatus } from '../upload-folder.model';
import { EmbedFilePayload } from '../../../insights-cube.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationDialogButton } from '../../../../../core/enums/confirmationDialogButton.enum';
import { ConfirmationDialog } from '../../../../../shared/components/confirmation-dialog/confirmation-dialog';
import { InsightsQubeService } from '../../../../../core/services/insights-qube.service';
import { AlertService } from '../../../../../core/services/alert.service';
import { MatDialog } from '@angular/material/dialog';
import { SequentialUploadService } from './sequential-upload.service';


@Injectable({
  providedIn: 'root'
})
export class UploadWidgetService {

  private readonly insQubeSrv = inject(InsightsQubeService);
  private readonly alertSrv = inject(AlertService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly matDialog = inject(MatDialog);



  readonly uploads = signal<UploadJob[]>([]);
  readonly isVisible = signal(false);
  readonly uploadCompleted = signal<{timestamp: number} | null>(null);

  readonly jobs =
    signal<UploadJob[]>([]);

  readonly cancelledJobs =
  signal<Set<string>>(
    new Set()
  );

  addJob(
    job: UploadJob
  ): void {

    this.jobs.update(
      jobs => [...jobs, job]
    );
  }

  updateJob(
    jobId: string,
    updater: (
      job: UploadJob
    ) => UploadJob
  ): void {

    this.jobs.update(
      jobs =>
        jobs.map(job =>
          job.id === jobId
            ? updater(job)
            : job
        )
    );

    console.log({"this.jobs": this.jobs()});
  }

  // removeJob(
  //   jobId: string
  // ): void {

  //   this.jobs.update(
  //     jobs =>
  //       jobs.filter(
  //         x => x.id !== jobId
  //       )
  //   );
  // }

  startAutoClose(
  jobId: string
): void {

  setTimeout(() => {

    this.removeJob(jobId);

  }, 5000);
}

removeJob(
  jobId: string
): void {

  this.jobs.update(
    jobs =>
      jobs.filter(
        job =>
          job.id !== jobId
      )
  );
}

clear(): void {
  this.jobs.set([]);
}

cancelJob(
  jobId: string
): void {

  this.cancelledJobs.update(
    jobs => {

      const next =
        new Set(jobs);

      next.add(jobId);

      return next;
    }
  );
}

isCancelled(
  jobId: string
): boolean {

  return this
    .cancelledJobs()
    .has(jobId);
}

markJobFailed(
  jobId: string,
  message: string,
  fatal = false
): void {

  this.updateJob(
    jobId,
    job => ({
      ...job,
      status: 'failed',
      errorMessage: message,
      fatalError: fatal
    })
  );
}

getCreatedFolderIds(
  jobId: string
): number[] {

  const job =
    this.jobs()
      .find(
        x => x.id === jobId
      );

  return (
    job?.createdFolders
      .map(
        folder => folder.id
      ) ?? []
  );
}

getCreatedFileIds(
  jobId: string
): number[] {

  const job =
    this.jobs()
      .find(
        x => x.id === jobId
      );

  return (
    job?.createdFileIds
      ?? []
  );
}

  // ======================================================
  // EMBED
  // ======================================================

confirmEmbed(
  jobId: string
): void {

  const job =
    this.getJob(jobId);

  if (!job) {
    return;
  }

  if (
    job.processingStatus ===
      'processing' ||

    job.processingStatus ===
      'completed'
  ) {
    return;
  }

  const dialogRef =
    this.matDialog.open(
      ConfirmationDialog,
      {
        disableClose: false,
        data: {
          title: 'Process Files',
          message:
            'Do you want to proceed with processing these files?',
          primaryButton: 'Process',
          secondaryButton: 'Cancel'
        }
      }
    );

  dialogRef.afterClosed()
    .pipe(
      takeUntilDestroyed(
        this.destroyRef
      )
    )
    .subscribe(
      result => {

        if (
          result ===
          ConfirmationDialogButton.PRIMARY
        ) {

          this.updateJob(
            jobId,
            current => ({

              ...current,

              processingStatus:
                'processing'
            })
          );

          this.embed(jobId);
        }
      }
    );
}

embed(
  jobId: string
): void {

  const payload: EmbedFilePayload = {

    dir_ids:
      this.getCreatedFolderIds(
        jobId
      )
  };

  this.insQubeSrv
    .embedFile(payload)
    .pipe(
      takeUntilDestroyed(
        this.destroyRef
      )
    )
    .subscribe({

      next: () => {

        this.updateJob(
          jobId,
          current => ({

            ...current,

            processingStatus:
              'completed'
          })
        );

        this.alertSrv.success(
          'Processing started successfully.'
        );
      },

      error: () => {

        this.updateJob(
          jobId,
          current => ({

            ...current,

            processingStatus:
              'failed'
          })
        );
      }
    });
}

  getJob(
  jobId: string
): UploadJob | undefined {

  return this.jobs()
    .find(
      job => job.id === jobId
    );
}

calculateStatus(
  uploadedFiles: number,
  failedFiles: number,
  skippedFiles: number,
  totalFiles: number
):UploadJobStatus {

  if (
    uploadedFiles === 0
    &&
    failedFiles > 0
  ) {
    return 'failed';
  }

  if (
    failedFiles > 0 ||
    skippedFiles > 0
  ) {
    return 'partial';
  }

  return 'completed';
}

}